// @flow
import type { SendAnalytics } from '../../core/ga';

import * as most from 'most';
import * as mA from 'most-adjunct';
import * as mostc from '../../crosscutting/mostc';
import { select } from 'redux-most';
import * as r from 'ramda';

import { Hook, AvailabilityStatus } from '../../core/constants';
import * as signals from '../signals';
import * as messages from '../../domain/messages';
import * as selectors from '../../domain/selectors';

const purchaseBundleEpic = (
  sendAnalytics: SendAnalytics,
) => (actions$, store) => r.pipe(
  select(signals.PURCHASE_BUNDLE),
  mostc.tap(() => {
    sendAnalytics({
      hitType: 'event',
      eventCategory: 'Basket',
      eventAction: 'bundle',
    });
  }),
  mA.switchMap((action) => {
    const state = store.getState();
    const { availability } = r.defaultTo({}, selectors.getExactMatch(state, {}));
    const includeExact = selectors.includeExactInBundle(state);
    const isExactInBasket = r.equals(AvailabilityStatus.AVAILABLE, availability);

    const filter = r.pathEq([ 'payload', 'hook' ], Hook.ADD_BUNDLE_TO_BASKET);

    // if the includeExact flag is set, we want to add the exact domain to the list of bundle fqdns
    const bundles = r.pipe(
      r.when(
        r.always(includeExact && !isExactInBasket),
        r.prepend(action.payload.fqdn),
      ),
    )(action.payload.bundles);

    const purchasingBundle$ = most.of(messages.purchasingBundle(
      action.payload.fqdn,
      bundles,
    ));

    // a bundle is linked to a specific domain
    // if we haven't already purchased the domain,
    // trigger a purchase signal for it
    // if the includeExact flag is set, we skip this step as we'll add the domain
    // as part of the bundle-adding event
    const purchaseDomain$ = r.ifElse(
      r.always(includeExact || isExactInBasket),
      r.always(most.empty()),
      r.always(most.of(
        signals.purchaseDomain(action.payload.fqdn),
      )),
    )(null);

    const emit$ = most.of(
      signals.emit(
        Hook.ADD_BUNDLE_TO_BASKET,
        {
          fqdns: bundles,
        },
        true,
      ),
    );

    const success$ = r.pipe(
      select(messages.EMIT_SUCCESS),
      mostc.filter(filter),
      mostc.map(
        r.always(
          messages.bundlePurchased(
            action.payload.fqdn,
            bundles,
          ),
        ),
      ),
    )(actions$);

    const failed$ = r.pipe(
      select(messages.EMIT_FAILED),
      mostc.filter(filter),
      mostc.map(
        r.always(
          messages.bundleFailed(
            action.payload.fqdn,
            bundles,
          ),
        ),
      ),
    )(actions$);

    return most.mergeArray([
      purchasingBundle$,
      purchaseDomain$,
      emit$,
      success$,
      failed$,
    ]);
  }),
)(actions$);

export default purchaseBundleEpic;
